import { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import { LoadingButton } from "@mui/lab";
import { useAddEventMutation } from "src/api/purchasing/events";
import { useGetPaymentMethods } from "src/api/generic";
import { useGetVendorsList } from "src/api/operation-cost/vendors";
import { useTranslation } from "react-i18next";
import FilePondUploader from "src/shared/components/file-pond-uploader";
import { convertObjectToFormData } from "src/utils";
import { useRecoilState, useRecoilValue } from "recoil";
import { notificationsState } from "src/store/notifications";
import { authState } from "src/store/auth";
import { useGetTreeAccountsList } from "src/api/tree/tree";

interface Props {
  open: boolean;
  handleClose: () => void;
  refetch: Function;
}

const schema = yup.object({
  mode: yup
    .string()
    .required("required")
    .oneOf(["add", "edit"] as const),
  vendor_id: yup.string().required("required"),
  direction: yup
    .string()
    .oneOf(["to_vendor", "from_vendor"])
    .required("required"),
  operation: yup.string().required("required"),
  payment_id: yup.string().when("operation", {
    is: (op: string) => ["refund", "advanced", "settlement"].includes(op),
    then: (v) => v.required("required"),
  }),
  account_id: yup.string().when("operation", {
    is: "expenses",
    then: (v) => v.required("required"),
  }),
  amount: yup.number().required("required"),
  date: yup.date().required("required"),
  note: yup.string(),
});

type FormInputs = yup.InferType<typeof schema>;

const VendorEventPopup = ({ open, handleClose, refetch }: Props) => {
  const { t } = useTranslation("purchasing");
  const { t: generalT } = useTranslation("general");
  const [files, setFiles] = useState<any>([]);
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const auth = useRecoilValue(authState);
  const company = auth?.user?.company;

  // apis
  const { mutate, isPending } = useAddEventMutation();
  const { data: paymentsData } = useGetPaymentMethods();
  const { data: accountData } = useGetTreeAccountsList({
    category_system_key: "expenses",
  });
  const { data: vendorsData } = useGetVendorsList();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
    setValue,
  } = useForm<FormInputs>({
    resolver: yupResolver(schema),
    defaultValues: {
      mode: "add",
    },
  });

  const direction = watch("direction");
  const operation = watch("operation");

  const getOperations = () => {
    if (direction === "to_vendor") {
      return [
        { value: "advanced", label: generalT("advancePayment") },
        { value: "settlement", label: t("settlement") },
        { value: "expenses", label: generalT("expenses") },
      ];
    }
    if (direction === "from_vendor") {
      return [
        { value: "refund", label: generalT("refund") },
        { value: "credit_note", label: generalT("creditNote") },
        { value: "other_revenue", label: generalT("otherRevenue") },
        { value: "expenses", label: generalT("expenses") },
      ];
    }
    return [];
  };

  const shouldShowPayment = [
    "refund",
    "advanced",
    "settlement",
    "other_revenue",
    "credit_note",
  ].includes(operation);

  const onClose = () => {
    handleClose();
    reset({});
  };

  const onSubmit = (data: FormInputs) => {
    // Balance date validation
    if (
      !!company?.start_balance_date &&
      moment(data.date)
        .startOf("day")
        .isBefore(moment(company.start_balance_date).startOf("day"))
    ) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: `${generalT("balanceDateValidationMsg")}: ${
            company?.start_balance_date
          }`,
        },
      ]);
      return;
    }

    const payload = {
      type: "vendor",
      operation: data.direction,
      method: data.operation,
      amount: +data.amount,
      date: moment(data.date).format("YYYY-MM-DD"),
      vendor_id: data.vendor_id,
      ...(shouldShowPayment &&
        data.direction === "to_vendor" && { payment_from_id: data.payment_id }),
      ...(shouldShowPayment &&
        data.direction === "from_vendor" && { payment_to_id: data.payment_id }),
      ...(operation === "expenses" && { chart_account_id: data.account_id }),

      ...(data.note && { note: data.note }),
    };

    const formData = convertObjectToFormData(payload);

    // append files
    if (files.length > 0) {
      files.forEach((file: any, index: number) => {
        formData.append(`files[${index}]`, file);
      });
    }

    mutate(formData, {
      onSuccess: () => {
        refetch();
        onClose();
      },
      onError: (error) => {
        setNotifications([
          ...notifications,
          {
            type: "error",
            message:
              error?.data?.errors?.[0].value || generalT("somethingWrong"),
          },
        ]);
      },
    });
  };

  useEffect(() => {
    setValue("operation", "");
  }, [direction]);

  return (
    <Modal open={open} onClose={onClose}>
      <Stack
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "100%", lg: "537px" },
          bgcolor: "#FFF",
          border: "1px solid #D0D5DD",
          borderRadius: 1,
          boxShadow: 24,
          p: 3,
        }}
      >
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Typography fontSize="20px">{t("vendorEvent")}</Typography>
          <IconButton onClick={onClose} sx={{ p: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap={2}>
            {/* Direction */}
            <FormControl size="small" error={!!errors.direction}>
              <Controller
                name="direction"
                control={control}
                render={({ field }) => (
                  <RadioGroup {...field} row>
                    <FormControlLabel
                      value="to_vendor"
                      control={<Radio size="small" />}
                      label={t("toVendor")}
                    />
                    <FormControlLabel
                      value="from_vendor"
                      control={<Radio size="small" />}
                      label={t("fromVendor")}
                    />
                  </RadioGroup>
                )}
              />
              <FormHelperText>{errors.direction?.message}</FormHelperText>
            </FormControl>

            {/* Select vendor */}
            <FormControl
              fullWidth
              size="small"
              error={!!errors.vendor_id}
              sx={{ mt: 1 }}
            >
              <InputLabel id="demo-simple-select-label">
                {generalT("vendor")}
              </InputLabel>
              <Controller
                name="vendor_id"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    key={field.value}
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    label={generalT("vendor")}
                  >
                    {vendorsData?.data.map((ven: any) => {
                      return (
                        <MenuItem key={ven.id} value={ven.id}>
                          {ven.name}
                        </MenuItem>
                      );
                    })}
                    {vendorsData?.data?.length === 0 ? (
                      <Typography align="center" color={"textSecondary"}>
                        {generalT("noData")}
                      </Typography>
                    ) : null}
                  </Select>
                )}
              />
              <FormHelperText id="my-helper-text">
                {errors.vendor_id?.message}
              </FormHelperText>
            </FormControl>

            {/* Operation */}
            {direction && (
              <FormControl size="small" error={!!errors.operation}>
                <Controller
                  name="operation"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup {...field} row>
                      {getOperations().map((op) => (
                        <FormControlLabel
                          key={op.value}
                          value={op.value}
                          control={<Radio size="small" />}
                          label={op.label}
                          disabled={op.value === "credit_note"}
                        />
                      ))}
                    </RadioGroup>
                  )}
                />
                <FormHelperText>{errors.operation?.message}</FormHelperText>
              </FormControl>
            )}

            {/* Payment */}
            {shouldShowPayment && (
              <FormControl
                fullWidth
                size="small"
                error={!!errors.payment_id}
                sx={{ mt: 1 }}
              >
                <InputLabel>Payment</InputLabel>
                <Controller
                  name="payment_id"
                  control={control}
                  render={({ field }) => (
                    <Select {...field} label="Payment">
                      {paymentsData?.data?.map((p: any) => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <FormHelperText>{errors.payment_id?.message}</FormHelperText>
              </FormControl>
            )}

            {/* Account (Expenses only) */}
            {operation === "expenses" && (
              <FormControl
                fullWidth
                size="small"
                error={!!errors.account_id}
                sx={{ mt: 1 }}
              >
                <InputLabel>{generalT("expenses")}</InputLabel>
                <Controller
                  name="account_id"
                  control={control}
                  render={({ field }) => (
                    <Select {...field} label={generalT("expenses")}>
                      {accountData?.data?.map((acc: any) => (
                        <MenuItem key={acc.id} value={acc.id}>
                          {acc.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <FormHelperText>{errors.account_id?.message}</FormHelperText>
              </FormControl>
            )}

            {/* Amount */}
            <Controller
              name="amount"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  size="small"
                  label={t("amount")}
                  fullWidth
                  sx={{ mt: 1 }}
                  error={!!errors.amount}
                  helperText={errors.amount?.message}
                />
              )}
            />

            {/* Note */}
            <Controller
              name="note"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  label={t("notesOptional")}
                  fullWidth
                  multiline
                  rows={3}
                  sx={{ mt: 1 }}
                />
              )}
            />

            {/* Date */}
            <Controller
              name="date"
              control={control}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  format={"dd/MM/yyyy"}
                  label={`${t("transactionDateRepeat")}`}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      error: !!errors.date,
                      helperText: errors.date?.message,
                      inputProps: {
                        readOnly: true,
                        onKeyDownCapture: (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        },
                        onPasteCapture: (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        },
                      },
                    },
                  }}
                />
              )}
            />

            <FilePondUploader
              onUpload={(e) => setFiles(e)}
              maxFiles={3}
              maxFileSize={3}
              acceptedFileTypes={["image/*", "application/pdf"]}
            />
          </Stack>

          <Stack direction="row" spacing={2} mt={3}>
            <Button variant="outlined" fullWidth onClick={onClose}>
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              fullWidth
              loading={isPending}
            >
              Submit
            </LoadingButton>
          </Stack>
        </form>
      </Stack>
    </Modal>
  );
};

export default VendorEventPopup;
